import ReactGA from "react-ga";
import { isNotEmpty } from "../js_utils/array_utils";
import { isEmpty } from "../js_utils/lang_utils";
import { stripUndefined } from "../js_utils/object_utils";
import { Analytics, EcommerceEvent, TrackProperties } from "./types";

export class GASourceProvider {
  trackers: GATracker[] = [];

  initialize(tracker: GAClient) {
    if (isEmpty(this.trackers)) {
      ReactGA.initialize([tracker], { alwaysSendToDefaultTracker: false });
      this.trackers.push(initTracker(tracker));
    } else {
      const existing = this.trackers.find(
        (t) => t.name === tracker.gaOptions.name
      );
      if (!existing) {
        ReactGA.addTrackers([tracker]);
        this.trackers.push(initTracker(tracker));
      }
    }

    return GAAnalytics(this.trackers);
  }
}

function initTracker(tracker: GAClient) {
  if (tracker.ecommerce) {
    ReactGA.ga(`${tracker.gaOptions.name}.require`, "ec");
  }
  if (tracker.linkers) {
    ReactGA.ga(`${tracker.gaOptions.name}.require`, "linker");
    ReactGA.ga(`${tracker.gaOptions.name}.linker:autoLink`, tracker.linkers);
  }

  return {
    name: tracker.gaOptions.name,
    ecommerce: !!tracker.ecommerce,
  };
}

interface GATracker {
  name: string;
  ecommerce: boolean;
}

interface GATrackerOptions {
  trackingId: string;
  ecommerce?: boolean;
  linkers?: string[];
  gaOptions: {
    name: string;
    storage?: string;
    clientId?: string;
    allowLinker?: boolean;
    cookieDomain?: string;
  };
}

export interface GAClient extends GATrackerOptions {
  provider: "ga";
}

export function makeGAClient(tracker: GATrackerOptions): GAClient {
  return {
    provider: "ga",
    linkers: tracker.linkers,
    ecommerce: tracker.ecommerce,
    trackingId: tracker.trackingId,
    gaOptions: {
      ...tracker.gaOptions,
      cookieDomain: "auto",
      ...(tracker.linkers &&
        isNotEmpty(tracker.linkers) && {
          allowLinker: true,
        }),
    },
  };
}

function GAAnalytics(trackers: GATracker[]): Analytics {
  return {
    group: () => {},
    register: () => {},
    identify: () => {},
    screen: (name) => {
      trackers.forEach((t) => {
        ReactGA.ga(`${t.name}.send`, {
          hitType: "pageview",
          page: window.location.pathname,
          ...(name && { title: name }),
        });
      });
    },
    ecommerceEvent: (event) => {
      trackers.forEach((t) => {
        if (t.ecommerce) {
          sendGAEcommerceEvent(event, t);
        }
      });
    },
    event: (eventName: string, properties: TrackProperties) => {
      trackers.forEach((tracker) => {
        ReactGA.ga(
          `${tracker.name}.send`,
          "event",
          properties.category,
          eventName,
          properties.label
        );
      });
    },
  };
}

function sendGAEcommerceEvent(event: EcommerceEvent, tracker: GATracker) {
  switch (event.event) {
    case "Product Viewed":
      const product = event.products[0];
      ReactGA.ga(
        `${tracker.name}.ec:addImpression`,
        stripUndefined({
          id: product.id,
          name: product.name,
          category: product.category,
          brand: product.brand,
          variant: product.variant,
          list: product.list,
          position: product.position,
        })
      );

      ReactGA.ga(`${tracker.name}.ec:setAction`, "detail", {});
      ReactGA.ga(
        `${tracker.name}.send`,
        "event",
        "Ecommerce",
        "Product Viewed",
        product.name
      );
      break;
    case "Checkout Started":
      if (event.currency) {
        ReactGA.ga(`${tracker.name}.set`, "currencyCode", event.currency);
      }
      event.products.forEach((p) => {
        ReactGA.ga(`${tracker.name}.ec:addProduct`, p);
      });
      ReactGA.ga(`${tracker.name}.ec:setAction`, "checkout", {});
      ReactGA.ga(
        `${tracker.name}.send`,
        "event",
        "Ecommerce",
        "Checkout Started",
        event.revenue ? String(event.revenue) : ""
      );
      break;
    case "Order Completed":
      if (event.currency) {
        ReactGA.ga(`${tracker.name}.set`, "currencyCode", event.currency);
      }
      event.products.forEach((p) => {
        ReactGA.ga(`${tracker.name}.ec:addProduct`, p);
      });

      ReactGA.ga(
        `${tracker.name}.ec:setAction`,
        "purchase",
        stripUndefined({
          id: event.id,
          affiliation: event.affiliation,
          revenue: event.revenue,
          tax: event.tax,
          shipping: event.shipping,
          coupon: event.coupon,
        })
      );
      ReactGA.ga(
        `${tracker.name}.send`,
        "event",
        "Ecommerce",
        "Order Completed",
        event.revenue ? String(event.revenue) : ""
      );
      break;
    default:
      console.error(`Analytics: event ${event.event} not supported yet.`);
      break;
  }
}
